import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SCHOOL_NAME, SCHOOL_LOCATION, SCHOOL_TAGLINE } from "@/lib/branding";
import Logo from "@/components/app/Logo";
import { LayoutDashboard, Users, ClipboardCheck, GraduationCap, FileSpreadsheet, LogOut, ShieldCheck, Receipt, UserCog, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "editor", "viewer"], testid: "nav-dashboard" },
  { to: "/inspections/new", label: "New Inspection", icon: ClipboardCheck, roles: ["admin", "editor"], testid: "nav-new-inspection" },
  { to: "/records", label: "Records", icon: FileSpreadsheet, roles: ["admin", "editor", "viewer"], testid: "nav-records" },
  { to: "/fines", label: "Fines & Repeats", icon: Receipt, roles: ["admin", "editor", "viewer"], testid: "nav-fines" },
  { to: "/students", label: "Students", icon: GraduationCap, roles: ["admin", "editor"], testid: "nav-students" },
  { to: "/users", label: "Staff Accounts", icon: Users, roles: ["admin"], testid: "nav-users" },
  { to: "/account", label: "My Account", icon: UserCog, roles: ["admin"], testid: "nav-account" },
];

export default function AppShell({ children, active }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar automatically when the route changes on mobile.
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const go = (to) => navigate(to);

  const NavItems = () => (
    <>
      {NAV.filter((n) => n.roles.includes(user?.role)).map((n) => {
        const Icon = n.icon;
        const isActive = active === n.to || (n.to !== "/" && active?.startsWith(n.to));
        return (
          <button
            key={n.to}
            data-testid={n.testid}
            onClick={() => go(n.to)}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200",
              isActive
                ? "bg-carmel-gold text-white shadow-card"
                : "text-white/75 hover:text-white hover:bg-white/5"
            )}
          >
            <Icon className="w-4 h-4" />
            <span>{n.label}</span>
          </button>
        );
      })}
    </>
  );

  const SidebarContent = () => (
    <>
      <div className="px-6 pt-6 pb-6 border-b border-white/10 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Logo className="w-12 h-12 shrink-0" testId="sidebar-logo" />
          <div className="min-w-0">
            <p className="font-serif text-lg leading-tight tracking-tight truncate">Carmel SSS</p>
            <p className="text-[11px] tracking-wider uppercase text-white/60 truncate">{SCHOOL_LOCATION}</p>
          </div>
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="md:hidden text-white/70 hover:text-white p-1 -mr-2"
          aria-label="Close menu"
          data-testid="sidebar-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <NavItems />
      </nav>

      <div className="px-4 py-4 border-t border-white/10">
        <div className="flex items-center gap-2 mb-3 text-xs text-white/60">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span className="uppercase tracking-widest">{user?.role}</span>
        </div>
        <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
        <p className="text-xs text-white/50 truncate">{user?.email}</p>
        <button
          data-testid="logout-button"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-md border border-white/10 text-xs text-white/80 hover:bg-white/5 transition-colors duration-200"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-carmel-slateBg text-carmel-navy">
      {/* Desktop sidebar (md+) */}
      <aside className="hidden md:flex w-64 shrink-0 bg-carmel-navy text-white flex-col grain-overlay">
        <SidebarContent />
      </aside>

      {/* Mobile drawer overlay */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={cn(
          "md:hidden fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-carmel-navy text-white flex flex-col grain-overlay z-50 transition-transform duration-200",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="mobile-sidebar"
      >
        <SidebarContent />
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-carmel-navy/10 px-4 md:px-8 py-3 md:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden w-10 h-10 rounded-md flex items-center justify-center text-carmel-navy hover:bg-carmel-navy/5 -ml-2"
              aria-label="Open menu"
              data-testid="menu-open"
            >
              <Menu className="w-6 h-6" />
            </button>
            <Logo className="w-11 h-11 md:w-16 md:h-16 shrink-0" framed={false} testId="header-logo" />
            <div className="min-w-0">
              <h1 className="font-serif text-base md:text-2xl tracking-tight text-carmel-navy leading-tight truncate" data-testid="page-school-name">
                {SCHOOL_NAME}
              </h1>
              <p className="hidden md:block text-[10px] uppercase tracking-[0.25em] text-carmel-navy/50">Inspection Console · {SCHOOL_LOCATION}</p>
              <p className="md:hidden text-[10px] uppercase tracking-widest text-carmel-navy/50 truncate">{SCHOOL_LOCATION}</p>
            </div>
          </div>
          <div className="hidden md:block text-right text-xs text-carmel-navy/60 shrink-0">
            <p className="font-medium text-carmel-navy">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
            <p>{new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</p>
          </div>
        </header>
        <div className="p-4 md:p-8 animate-fade-in-up">{children}</div>
      </main>
    </div>
  );
}
